import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import type { Channel } from '../services/channelService';

interface ChannelPlayerModalProps {
  channel: Channel;
  onClose: () => void;
}

interface QualityLevel {
  id: number;
  label: string;
  width: number;
  height: number;
  bitrate: number;
  enabled: boolean;
}

interface StreamStats {
  startTime: Date;
  duration: string;
  buffered: number;
  currentTime: number;
  errors: string[];
  state: 'connecting' | 'playing' | 'buffering' | 'error' | 'ended';
  bandwidth?: number;
  resolution?: string;
  bitrate?: string;
}

const ChannelPlayerModal: React.FC<ChannelPlayerModalProps> = ({
  channel,
  onClose,
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
  const startTimeRef = useRef<Date>(new Date());
  const [stats, setStats] = useState<StreamStats>({
    startTime: new Date(),
    duration: '00:00:00',
    buffered: 0,
    currentTime: 0,
    errors: [],
    state: 'connecting',
  });
  const [qualityLevels, setQualityLevels] = useState<QualityLevel[]>([]);
  const [selectedQuality, setSelectedQuality] = useState<number | 'auto'>('auto');
  const [showQualityMenu, setShowQualityMenu] = useState(false);

  // Format duration as HH:MM:SS
  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Update duration every second
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000);
      setStats(prev => ({
        ...prev,
        duration: formatDuration(elapsed),
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Initialize video.js player
  useEffect(() => {
    if (!videoRef.current) return;

    const streamUrl = channel.hls_master_url || channel.hls_720p_url || channel.hls_1080p_url;
    
    if (!streamUrl) {
      setStats(prev => ({
        ...prev,
        state: 'error',
        errors: [...prev.errors, 'No stream URL available'],
      }));
      return;
    }

    // Create video element
    const videoElement = document.createElement('video-js');
    videoElement.classList.add('vjs-big-play-centered', 'vjs-theme-city');
    videoRef.current.appendChild(videoElement);

    // Initialize player
    const player = videojs(videoElement, {
      autoplay: true,
      controls: true,
      responsive: true,
      fluid: true,
      liveui: true,
      html5: {
        vhs: {
          overrideNative: true,
          enableLowInitialPlaylist: true,
          smoothQualityChange: true,
        },
      },
      sources: [{
        src: streamUrl,
        type: 'application/x-mpegURL',
      }],
    });

    playerRef.current = player;

    // Event handlers
    player.on('loadstart', () => {
      setStats(prev => ({ ...prev, state: 'connecting' }));
    });

    player.on('waiting', () => {
      setStats(prev => ({ ...prev, state: 'buffering' }));
    });

    player.on('playing', () => {
      setStats(prev => ({ ...prev, state: 'playing' }));
    });

    player.on('ended', () => {
      setStats(prev => ({ ...prev, state: 'ended' }));
    });

    player.on('error', () => {
      const error = player.error();
      setStats(prev => ({
        ...prev,
        state: 'error',
        errors: [...prev.errors, error?.message || 'Unknown error'],
      }));
    });

    // Get quality levels when loaded
    player.on('loadedmetadata', () => {
      const tech = player.tech({ IWillNotUseThisInPlugins: true }) as any;
      if (tech && tech.vhs && tech.vhs.representations) {
        const reps = tech.vhs.representations();
        if (reps && reps.length > 0) {
          const levels: QualityLevel[] = reps.map((rep: any, idx: number) => ({
            id: idx,
            label: `${rep.height}p`,
            width: rep.width,
            height: rep.height,
            bitrate: rep.bandwidth,
            enabled: rep.enabled !== false,
          }));
          // Sort by height descending
          levels.sort((a, b) => b.height - a.height);
          setQualityLevels(levels);
        }
      }
    });

    // Update stats periodically
    const statsInterval = setInterval(() => {
      if (player && !player.isDisposed()) {
        const buffered = player.buffered();
        const currentTime = player.currentTime() || 0;
        let bufferedEnd = 0;
        
        if (buffered && buffered.length > 0) {
          bufferedEnd = buffered.end(buffered.length - 1);
        }

        // Get tech info if available
        const tech = player.tech({ IWillNotUseThisInPlugins: true }) as any;
        let bandwidth: number | undefined;
        let resolution: string | undefined;
        let bitrate: string | undefined;

        if (tech && tech.vhs) {
          const vhs = tech.vhs;
          if (vhs.bandwidth) {
            bandwidth = Math.round(vhs.bandwidth / 1000); // kbps
          }
          if (vhs.playlists && vhs.playlists.media_) {
            const media = vhs.playlists.media_;
            if (media.attributes) {
              if (media.attributes.RESOLUTION) {
                resolution = `${media.attributes.RESOLUTION.width}x${media.attributes.RESOLUTION.height}`;
              }
              if (media.attributes.BANDWIDTH) {
                bitrate = `${Math.round(media.attributes.BANDWIDTH / 1000)} kbps`;
              }
            }
          }
        }

        setStats(prev => ({
          ...prev,
          currentTime,
          buffered: bufferedEnd - currentTime,
          bandwidth,
          resolution,
          bitrate,
        }));
      }
    }, 1000);

    // Cleanup
    return () => {
      clearInterval(statsInterval);
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [channel]);

  // Switch quality level
  const switchQuality = (qualityId: number | 'auto') => {
    const player = playerRef.current;
    if (!player) return;

    const tech = player.tech({ IWillNotUseThisInPlugins: true }) as any;
    if (!tech || !tech.vhs || !tech.vhs.representations) return;

    const reps = tech.vhs.representations();
    if (!reps) return;

    if (qualityId === 'auto') {
      // Enable all representations for auto
      reps.forEach((rep: any) => rep.enabled(true));
    } else {
      // Find the representation with matching height and enable only that one
      const targetLevel = qualityLevels.find(q => q.id === qualityId);
      if (targetLevel) {
        reps.forEach((rep: any) => {
          rep.enabled(rep.height === targetLevel.height);
        });
      }
    }
    
    setSelectedQuality(qualityId);
    setShowQualityMenu(false);
  };

  // Get label for current quality
  const getQualityLabel = () => {
    if (selectedQuality === 'auto') {
      return stats.resolution ? `Auto (${stats.resolution.split('x')[1]}p)` : 'Auto';
    }
    const level = qualityLevels.find(q => q.id === selectedQuality);
    return level ? level.label : 'Auto';
  };

  // State colors
  const stateColors = {
    connecting: 'bg-yellow-100 text-yellow-800',
    playing: 'bg-green-100 text-green-800',
    buffering: 'bg-blue-100 text-blue-800',
    error: 'bg-red-100 text-red-800',
    ended: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-gray-900 rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden m-4">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-4 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
            <h2 className="text-xl font-bold text-white">{channel.name}</h2>
            <span className={`px-2 py-1 rounded text-xs font-medium ${stateColors[stats.state]}`}>
              {stats.state.toUpperCase()}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Video Player */}
        <div className="bg-black aspect-video relative">
          <div ref={videoRef} className="w-full h-full" />
          
          {/* Quality Selector Overlay */}
          {qualityLevels.length > 0 && (
            <div className="absolute bottom-4 right-4 z-10">
              <div className="relative">
                <button
                  onClick={() => setShowQualityMenu(!showQualityMenu)}
                  className="bg-black/70 hover:bg-black/90 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {getQualityLabel()}
                  <svg className={`w-4 h-4 transition-transform ${showQualityMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Quality Menu */}
                {showQualityMenu && (
                  <div className="absolute bottom-full right-0 mb-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
                    <button
                      onClick={() => switchQuality('auto')}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-700 transition-colors flex items-center justify-between ${
                        selectedQuality === 'auto' ? 'bg-blue-600 text-white' : 'text-gray-200'
                      }`}
                    >
                      <span>Auto</span>
                      {selectedQuality === 'auto' && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <div className="border-t border-gray-700" />
                    {qualityLevels.map((level) => (
                      <button
                        key={level.id}
                        onClick={() => switchQuality(level.id)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-700 transition-colors flex items-center justify-between ${
                          selectedQuality === level.id ? 'bg-blue-600 text-white' : 'text-gray-200'
                        }`}
                      >
                        <span>{level.label}</span>
                        <span className="text-xs text-gray-400 ml-2">
                          {Math.round(level.bitrate / 1000)}k
                        </span>
                        {selectedQuality === level.id && (
                          <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stats Panel */}
        <div className="bg-gray-800 px-6 py-4">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">Stream Statistics</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Watch Duration */}
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Watch Duration</p>
              <p className="text-lg font-mono font-bold text-green-400">{stats.duration}</p>
            </div>

            {/* Buffer Health */}
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Buffer</p>
              <p className="text-lg font-mono font-bold text-blue-400">
                {stats.buffered.toFixed(1)}s
              </p>
            </div>

            {/* Resolution */}
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Resolution</p>
              <p className="text-lg font-mono font-bold text-purple-400">
                {stats.resolution || 'N/A'}
              </p>
            </div>

            {/* Bitrate */}
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Bitrate</p>
              <p className="text-lg font-mono font-bold text-orange-400">
                {stats.bitrate || 'N/A'}
              </p>
            </div>

            {/* Bandwidth */}
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Bandwidth</p>
              <p className="text-lg font-mono font-bold text-cyan-400">
                {stats.bandwidth ? `${stats.bandwidth} kbps` : 'N/A'}
              </p>
            </div>

            {/* Errors */}
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Errors</p>
              <p className={`text-lg font-mono font-bold ${stats.errors.length > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {stats.errors.length}
              </p>
            </div>
          </div>

          {/* Error Log */}
          {stats.errors.length > 0 && (
            <div className="mt-4 bg-red-900/30 border border-red-700 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-red-400 mb-2">Error Log</h4>
              <div className="max-h-24 overflow-y-auto space-y-1">
                {stats.errors.map((error, idx) => (
                  <p key={idx} className="text-xs text-red-300 font-mono">
                    [{new Date().toLocaleTimeString()}] {error}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Stream URL */}
          <div className="mt-4 bg-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-400 mb-1">Stream URL</p>
            <p className="text-sm font-mono text-gray-300 break-all">
              {channel.hls_master_url || channel.hls_720p_url || channel.hls_1080p_url || 'No URL'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelPlayerModal;

